/**
 * Scenario Chatbot Component
 *
 * IMPORTANT: This component now uses the INTENT-BASED PROMPT system by default.
 *
 * How it works:
 * 1. User sends a request (e.g., "Add a welcome message")
 * 2. Backend sends simplified prompt to LLM
 * 3. LLM outputs intent JSON with <<<INTENT>>> markers:
 *    { "operations": [{ "type": "ADD_NODE", "nodeType": "message:info", ... }] }
 * 4. Backend parses intent and stores in message.metadata.parsed.scenarioIntent
 * 5. Frontend receives pre-parsed intent and displays IntentPreview
 * 6. When user clicks "Apply Changes", intent processor converts to patches
 * 7. Patches are applied to scenario
 *
 * Benefits:
 * - Smaller prompt (~400 lines vs 1,347 lines) = 70% reduction
 * - LLM focuses on WHAT (intent) instead of HOW (patches)
 * - Backend parsing (consistent with patch-based system)
 * - On-demand patch generation (only when user applies)
 * - Deterministic patch generation (code handles complexity)
 * - Automatic bidirectional linking (no more LLM errors)
 * - Easier to maintain and extend
 *
 * Files:
 * - Backend: lib/utils/responseParser.js - Parses intent from LLM response
 * - Backend: lib/services/ChatService.js - Stores parsed intent in metadata
 * - Frontend: generateSimplifiedLLMPrompt.js - Simplified prompt
 * - Frontend: scenarioIntentProcessor.js - Intent to patches converter
 * - Frontend: parseIntentResponse.js - Client-side fallback for old messages
 *
 * Note: Still supports old patch-based responses for backwards compatibility.
 */

import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { Button, Dropdown, message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import ApiScenarioChatbot from '../../../../api/scenario-chatbot';
import { applyPatchesToScenario, repairScenarioBidirectionalLinks, validateScenarioStructure } from '../utils/scenarioUtils';
import { generateSimplifiedRuntimePrompt } from '../utils/generateSimplifiedLLMPrompt'; // New intent-based prompt
import { parseIntentFromLLMResponse } from '../utils/parseIntentResponse';
import { processScenarioIntent } from '../utils/scenarioIntentProcessor';
import { useProgressTracking } from '../hooks/useProgressTracking';
import ChatInputAreaWithUpload from '../../../reports/components/reports-ai-core/ChatInputAreaWithUpload';
import './scenario-chatbot.css';

import { buildSnapshot } from '../utils/scenarioSnapshot';

const ScenarioChatbot = ({
  currentScenario,
  tasks = [],
  userId, // Get userId from props (passed from parent)
  scenarioId, // Scenario ID to create separate sessions per scenario
  versionId, // Version ID for more specific session management
  nodesConfig = [],
  nodesDefinitions = {},
  onScenarioUpdate,
  onClose,
  fetchers = {},
}) => {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingStartTime, setLoadingStartTime] = useState(null);
  const [loadingDuration, setLoadingDuration] = useState(0);
  const [currentRequestId, setCurrentRequestId] = useState(null);
  const [error, setError] = useState(null);
  const [errorMessageIndex, setErrorMessageIndex] = useState(null); // Track which message caused the error for retry
  const [lastPatchErrors, setLastPatchErrors] = useState(null); // Track errors from last patch to provide feedback to LLM
  const [loadedDynamicData, setLoadedDynamicData] = useState(null);
  const getAllFitFacts = fetchers.getAllFitFacts;
  const getAllArticles = fetchers.getAllArticles;
  const dynamicData = loadedDynamicData && loadedDynamicData.scenarioId === scenarioId
    && loadedDynamicData.getAllFitFacts === getAllFitFacts
    && loadedDynamicData.getAllArticles === getAllArticles ? loadedDynamicData.data : null;
  // Bumped each time the chat is reset for another scenario or version. Ids
  // alone are not enough: after v1 → v2 → v1 work started in the first v1
  // would look current again.
  const viewGenerationRef = useRef(0);
  const currentView = () => {
    const generation = viewGenerationRef.current;
    return () => viewGenerationRef.current === generation;
  };
  // The request whose answer the chat is waiting for; an older one's answer,
  // even in the same view, must not land or end the newer one's loading.
  const requestSeqRef = useRef(0);
  const [pendingIntents, setPendingIntents] = useState({}); // Map of messageIndex -> {intent, parsed} for unapplied intent-based changes
  const [includeHistory, setIncludeHistory] = useState(() => {
    // Load history preference from localStorage
    const stored = localStorage.getItem('scenarioChatbot_includeHistory');
    return stored ? JSON.parse(stored) : true; // Default to true (include history)
  });
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Load collapsed state from localStorage
    const stored = localStorage.getItem('scenarioChatbot_collapsed');
    return stored ? JSON.parse(stored) : false;
  });
  const [position, setPosition] = useState(() => {
    // Load position from localStorage
    const stored = localStorage.getItem('scenarioChatbot_position');
    const parsed = stored ? JSON.parse(stored) : null;

    // Validate position is within viewport
    if (parsed) {
      const isValid = parsed.x >= 0 &&
                      parsed.y >= 0 &&
                      parsed.x < window.innerWidth - 100 &&
                      parsed.y < window.innerHeight - 100;

      if (!isValid) {
        localStorage.removeItem('scenarioChatbot_position');
        return null;
      }
    }

    return parsed;
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [size, setSize] = useState(() => {
    const stored = localStorage.getItem('scenarioChatbot_size');
    return stored ? JSON.parse(stored) : { width: 500, height: 600 };
  });
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });
  const messagesEndRef = useRef(null);
  const chatbotRef = useRef(null);
  const abortControllerRef = useRef(null);
  const loadingTimerRef = useRef(null);
  const pendingRequestRef = useRef(null);

  // Use progress tracking hook
  const { stageName, elapsed, isConnected } = useProgressTracking(currentRequestId);

  // Prefer server-provided elapsed time, but fall back to local timer so the counter always moves
  const elapsedSeconds = Math.max(
    elapsed ? elapsed / 1000 : 0,
    loadingDuration || 0
  );
  const elapsedDisplaySeconds = Math.max(0, Math.round(elapsedSeconds));

  // Always use the parent's current scenario value
  // Use a ref to avoid stale closures in async functions
  const currentScenarioRef = useRef(currentScenario);

  // Keep the ref in sync with the parent prop - parent is always the source of truth
  useEffect(() => {
    currentScenarioRef.current = currentScenario;
  }, [currentScenario]);

  // Send pending request when SSE connection is established
  useEffect(() => {
    if (isConnected && pendingRequestRef.current) {
      console.log('[ScenarioChatbot] SSE connected, sending pending request');
      const pendingRequest = pendingRequestRef.current;
      pendingRequestRef.current = null; // Clear pending request

      // Execute the pending request
      pendingRequest();
    }
  }, [isConnected]);

  // Expand chatbot when it's first opened
  useEffect(() => {
    // When the component mounts, expand it by default
    setIsCollapsed(false);
  }, []);

  // Fetch dynamic data (articles, facts, etc.) when component mounts
  useEffect(() => {
    let current = true;
    const loaded = (data) => {
      if (current) setLoadedDynamicData({ scenarioId, getAllFitFacts, getAllArticles, data });
    };
    const fetchDynamicData = async () => {
      try {
        const results = {};

        // Fetch fit facts if fetcher is available
        if (getAllFitFacts) {
          const factsResponse = await getAllFitFacts();
          results.fitFacts = factsResponse?.data || { ids: [], categories: [], tags: [] };
        }

        // Fetch articles if fetcher is available
        if (getAllArticles) {
          const articlesResponse = await getAllArticles();
          results.articles = articlesResponse?.data || { ids: [], categories: [], tags: [] };
        }

        loaded(results);
      } catch (err) {
        console.error('ScenarioChatbot: Error fetching dynamic data:', err);
        // Set empty data on error to allow chatbot to still function
        loaded({ fitFacts: { ids: [], categories: [], tags: [] }, articles: { ids: [], categories: [], tags: [] } });
      }
    };

    fetchDynamicData();
    return () => { current = false; };
  }, [scenarioId, getAllFitFacts, getAllArticles]);

  // Initialize or resume chat session when scenario changes
  useEffect(() => {
    let current = true;
    viewGenerationRef.current += 1;
    setSessionId(null);
    setMessages([]);
    // Pending intents are keyed by message index, and patch errors describe the
    // previous graph: neither may carry over into another scenario's chat.
    setPendingIntents({});
    setLastPatchErrors(null);
    setError(null);
    setErrorMessageIndex(null);
    pendingRequestRef.current = null;
    if (userId && scenarioId && dynamicData) {
      initializeSession(() => current);
    } else {
      setLoading(Boolean(userId && scenarioId));
    }
    return () => { current = false; };
  }, [userId, scenarioId, versionId, dynamicData]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Track blob URLs for cleanup
  const blobUrlsRef = useRef(new Map());

  // Cleanup all blob URLs on component unmount
  useEffect(() => {
    return () => {
      blobUrlsRef.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      blobUrlsRef.current.clear();
    };
  }, []);

  // Track loading duration
  useEffect(() => {
    if (loading && loadingStartTime) {
      const updateDuration = () => {
        setLoadingDuration((Date.now() - loadingStartTime) / 1000);
      };

      // Update immediately so we don't wait for the first interval tick
      updateDuration();
      loadingTimerRef.current = setInterval(updateDuration, 1000);
    } else {
      if (loadingTimerRef.current) {
        clearInterval(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      setLoadingDuration(0);
    }

    return () => {
      if (loadingTimerRef.current) {
        clearInterval(loadingTimerRef.current);
      }
    };
  }, [loading, loadingStartTime]);

  // Save collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('scenarioChatbot_collapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  // Save history preference to localStorage
  useEffect(() => {
    localStorage.setItem('scenarioChatbot_includeHistory', JSON.stringify(includeHistory));
  }, [includeHistory]);

  // Save position to localStorage
  useEffect(() => {
    if (position) {
      localStorage.setItem('scenarioChatbot_position', JSON.stringify(position));
    }
  }, [position]);

  // Save size to localStorage
  useEffect(() => {
    localStorage.setItem('scenarioChatbot_size', JSON.stringify(size));
  }, [size]);

  // Handle resize functionality
  useEffect(() => {
    const handleResizeMove = (e) => {
      if (!isResizing) return;

      const deltaX = e.clientX - resizeStartRef.current.x;
      const deltaY = e.clientY - resizeStartRef.current.y;

      const newWidth = Math.max(350, Math.min(resizeStartRef.current.width + deltaX, window.innerWidth * 0.9));
      const newHeight = Math.max(400, Math.min(resizeStartRef.current.height + deltaY, window.innerHeight * 0.9));

      setSize({ width: newWidth, height: newHeight });
    };

    const handleResizeEnd = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'nwse-resize';
    }

    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      if (!isDragging) {
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      }
    };
  }, [isResizing, isDragging]);

  // Handle drag functionality
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;

      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      // Get chatbot dimensions
      const chatbot = chatbotRef.current;
      if (!chatbot) return;

      const rect = chatbot.getBoundingClientRect();
      const maxX = window.innerWidth - rect.width;
      const maxY = window.innerHeight - rect.height;

      // Constrain to viewport
      const constrainedX = Math.max(0, Math.min(newX, maxX));
      const constrainedY = Math.max(0, Math.min(newY, maxY));

      setPosition({ x: constrainedX, y: constrainedY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'move';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging, dragOffset]);

  const handleMouseDown = (e) => {
    // Only allow dragging from the header, not from buttons
    if (e.target.closest('button') || e.target.closest('svg')) {
      return;
    }

    const chatbot = chatbotRef.current;
    if (!chatbot) return;

    const rect = chatbot.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!loading && sessionId && !isCollapsed) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we are leaving the container
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (loading || !sessionId || isCollapsed) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const validFiles = [];

    files.forEach(file => {
      // Image types
      const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];

      // Document types
      const documentTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/html',
        'text/plain'
      ];
      const documentExtensions = ['.pdf', '.docx', '.html', '.txt'];

      const isImage = imageTypes.includes(file.type) ||
                      imageExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      const isDocument = documentTypes.includes(file.type) ||
                         documentExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

      if (!isImage && !isDocument) {
        message.error(`${file.name}: Only PDF, DOCX, HTML, TXT, JPG, PNG, GIF, WebP, and SVG files are supported`);
        return;
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        message.error(`${file.name}: File must be smaller than 10MB`);
        return;
      }

      validFiles.push(file);
    });

    if (validFiles.length > 0) {
      // Create blob URL immediately for images
      validFiles.forEach(file => {
        const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
        const isImage = imageTypes.includes(file.type) ||
                        imageExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

        if (isImage) {
          const blobUrl = URL.createObjectURL(file);
          blobUrlsRef.current.set(file, blobUrl);
        }
      });

      setAttachedFiles(prev => [...prev, ...validFiles]);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const buildSystemPrompt = () => {
    // Use simplified intent-based prompt by default
    return generateSimplifiedRuntimePrompt(
      tasks,
      nodesDefinitions,
      dynamicData
    );
  };

  const mapApiMessagesToUi = (items = []) => {
    return items.map((m) => {
      const rawRole = (m.role || m.sender || m.author || 'assistant').toString().toLowerCase();
      const role = rawRole.includes('assistant') ? 'assistant' : rawRole.includes('user') ? 'user' : 'system';

      let content = m.content || m.text || m.message || '';

      // For assistant messages, use parsed explanation if available (removes markers)
      if (role === 'assistant') {
        const parsedData = m.metadata?.parsed || m.parsed;

        // Check if we have parsed data from backend
        if (parsedData) {
          // Use explanation if provided
          if (parsedData.explanation) {
            content = parsedData.explanation;
          }
          // If explanation is null but we have structured output, generate default
          else if (parsedData.hasIntent || parsedData.hasPatches || parsedData.hasPayload) {
            if (parsedData.scenarioIntent?.operations) {
              const opCount = parsedData.scenarioIntent.operations.length;
              content = `Applying ${opCount} operation${opCount !== 1 ? 's' : ''}`;
            } else if (parsedData.hasPatches) {
              content = 'Applying scenario patches';
            } else if (parsedData.hasPayload) {
              content = 'Updating scenario';
            }
          }
          // If no structured output, try parsing raw content as fallback
          else {
            const intentParsed = parseIntentFromLLMResponse(content);
            if (intentParsed.explanation) {
              content = intentParsed.explanation;
            } else if (intentParsed.intent) {
              // Generate explanation from parsed intent
              const opCount = intentParsed.intent.operations?.length || 0;
              content = opCount > 0 ? `Applying ${opCount} operation${opCount !== 1 ? 's' : ''}` : content;
            }
          }
        }
        // No parsed data at all - parse on the fly for old messages
        else if (content) {
          const intentParsed = parseIntentFromLLMResponse(content);
          if (intentParsed.explanation) {
            content = intentParsed.explanation;
          } else if (intentParsed.intent) {
            // Generate explanation from parsed intent
            const opCount = intentParsed.intent.operations?.length || 0;
            content = opCount > 0 ? `Applying ${opCount} operation${opCount !== 1 ? 's' : ''}` : content;
          }
        }
      }

      // Map attachments from backend message
      // For user messages, extract attachment metadata
      // For assistant messages, use processedAttachments field and patches
      const mappedMessage = {
        role,
        content,
        timestamp: m.createdAt || m.timestamp || new Date(),
        scenarioSnapshot: m.scenarioSnapshot || null, // Include scenario snapshot from backend
      };

      // Add attachments for user messages
      if (role === 'user' && m.attachments && Array.isArray(m.attachments) && m.attachments.length > 0) {
        mappedMessage.attachments = m.attachments.map(att => ({
          name: att.fileName || att.name,
          size: att.metadata?.size || att.size,
          type: att.mimeType || att.type
        }));
      }

      // Add processed attachments for assistant messages
      if (role === 'assistant' && m.processedAttachments && Array.isArray(m.processedAttachments)) {
        mappedMessage.processedAttachments = m.processedAttachments;
      }

      // Extract patches and intent for assistant messages from history
      if (role === 'assistant') {
        // Try to get patches from metadata.parsed first
        if (m.metadata?.parsed?.scenarioPatches) {
          mappedMessage.patches = m.metadata.parsed.scenarioPatches;
          mappedMessage.patchError = m.metadata.parsed.patchesError;
        }
        // Try m.parsed (direct parsed field)
        else if (m.parsed?.scenarioPatches) {
          mappedMessage.patches = m.parsed.scenarioPatches;
          mappedMessage.patchError = m.parsed.patchesError;
        }

        // Try to get intent from metadata.parsed first (new intent-based system)
        if (m.metadata?.parsed?.scenarioIntent) {
          mappedMessage.intent = m.metadata.parsed.scenarioIntent;
          mappedMessage.intentError = m.metadata?.parseIntentError;
        }
        // Try m.parsed (direct parsed field)
        else if (m.parsed?.scenarioIntent) {
          mappedMessage.intent = m.parsed.scenarioIntent;
          mappedMessage.intentError = m.parsed.intentError;
        }
      }

      return mappedMessage;
    });
  };

  const loadMessagesForSession = async (id, isCurrent = currentView()) => {
    try {
      const history = await ApiScenarioChatbot.getMessages(id, { limit: 100, offset: 0 });
      if (!isCurrent()) return;
      const payload = history?.data?.data || {};
      const rawMessages = Array.isArray(payload.messages)
        ? payload.messages
        : Array.isArray(payload.items)
          ? payload.items
          : Array.isArray(history?.data?.messages)
            ? history.data.messages
            : [];

      // Sort by messageIndex if present, else by createdAt
      const sorted = [...rawMessages].sort((a, b) => {
        const ai = typeof a.messageIndex === 'number' ? a.messageIndex : Number.MAX_SAFE_INTEGER;
        const bi = typeof b.messageIndex === 'number' ? b.messageIndex : Number.MAX_SAFE_INTEGER;
        if (ai !== bi) return ai - bi;
        const at = new Date(a.createdAt || a.timestamp || 0).getTime();
        const bt = new Date(b.createdAt || b.timestamp || 0).getTime();
        return at - bt;
      });

      const mapped = mapApiMessagesToUi(sorted);
      if (mapped.length > 0) {
        setMessages(mapped);
      } else {
        setMessages([
          {
            role: 'assistant',
            content: 'Hello! I can help you modify your scenario. Just tell me what you\'d like to change, for example:\n\n- "Add a welcome message at the beginning"\n- "Add a question asking about fitness level"\n- "Remove the cardio task"\n- "Change the goodbye message to something friendlier"',
            timestamp: new Date(),
          },
        ]);
      }
    } catch (e) {
      if (!isCurrent()) return;
      console.warn('Failed to load session messages, starting fresh:', e);
      setMessages([
        {
          role: 'assistant',
          content: 'Hello! I can help you modify your scenario. Just tell me what you\'d like to change, for example:\n\n- "Add a welcome message at the beginning"\n- "Add a question asking about fitness level"\n- "Remove the cardio task"\n- "Change the goodbye message to something friendlier"',
          timestamp: new Date(),
        },
      ]);
    }
  };

  const createNewSession = async (isCurrent = currentView()) => {
    if (!dynamicData || !isCurrent()) return;
    // Generate a system prompt and create a fresh session
    const systemPrompt = buildSystemPrompt();
    const response = await ApiScenarioChatbot.createSession(
      userId,
      `Scenario Editing: ${scenarioId}`,
      systemPrompt,
      {
        scenarioContext: 'scenario-editor',
        scenarioId: scenarioId,
        versionId: versionId,
        taskCount: tasks.length,
      }
    );

    if (isCurrent() && response.data.success) {
      const newId = response.data.data.id;
      setSessionId(newId);
      setMessages([
        {
          role: 'assistant',
          content: 'Hello! I can help you modify your scenario. Just tell me what you\'d like to change, for example:\n\n- "Add a welcome message at the beginning"\n- "Add a question asking about fitness level"\n- "Remove the cardio task"\n- "Change the goodbye message to something friendlier"',
          timestamp: new Date(),
        },
      ]);
    }
  };

  const initializeSession = async (isCurrent) => {
    try {
      setLoading(true);
      setError(null);
      setErrorMessageIndex(null);

      // Try to resume the latest existing session for this user and scenario
      const listResp = await ApiScenarioChatbot.listSessions(userId, { limit: 50, offset: 0 });
      if (!isCurrent()) return;
      const allSessions = listResp?.data?.data?.sessions || listResp?.data?.data || listResp?.data?.items || [];

      // Filter sessions for the current scenario
      // Try both metadata.scenarioId and title matching as fallback
      const scenarioSessions = Array.isArray(allSessions)
        ? allSessions.filter(session => {
            const metadata = session.metadata || {};
            const title = session.title || '';

            // Primary: Match by scenarioId in metadata
            const metadataMatch = metadata.scenarioId === scenarioId;

            // Fallback: Match by title containing scenarioId
            // Title format: "Scenario Editing: {scenarioId}"
            const titleMatch = title.includes(scenarioId);

            return metadataMatch || titleMatch;
          })
        : [];

      if (scenarioSessions.length > 0) {
        // Use the most recent session for this scenario
        const latest = scenarioSessions[0];
        const id = latest.id || latest._id;
        if (id) {
          setSessionId(id);
          await loadMessagesForSession(id, isCurrent);
          return;
        }
      }

      // No sessions found for this scenario - auto-create one
      await createNewSession(isCurrent);
    } catch (err) {
      if (!isCurrent()) return;
      console.error('Failed to initialize session:', err);
      setError('Failed to load existing sessions');
      setSessionId(null);
      setMessages([
        {
          role: 'assistant',
          content: 'Unable to load existing sessions. You can still start a new one by clicking "New session".',
          timestamp: new Date(),
        },
      ]);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if ((!inputMessage.trim() && attachedFiles.length === 0) || loading || !sessionId) return;

    const userMessage = inputMessage.trim() || '(Attached files)';
    const filesToSend = [...attachedFiles];
    setInputMessage('');

    // Revoke blob URLs for image files before clearing
    attachedFiles.forEach(file => {
      if (file.type.startsWith('image/')) {
        const url = blobUrlsRef.current.get(file);
        if (url) {
          URL.revokeObjectURL(url);
          blobUrlsRef.current.delete(file);
        }
      }
    });

    setAttachedFiles([]);
    setError(null);
    setErrorMessageIndex(null);

    // Create snapshot of current scenario for this message
    // Use ref to avoid stale closure - ref always has the latest value from parent
    const currentScenario = currentScenarioRef.current;
    const messageSnapshot = buildSnapshot(currentScenario, tasks);

    // Build message with optional error context
    let messageWithContext = userMessage;

    // Append error context from previous patch if there were errors
    if (lastPatchErrors) {
      const errorContext = `\n\n---\nIMPORTANT - Errors from your previous response that need to be fixed:\n${lastPatchErrors}\n---`;
      messageWithContext = messageWithContext + errorContext;

      // Clear the errors after including them
      setLastPatchErrors(null);
    }

    // Add user message to UI with snapshot and attachment info (show original message without error context)
    setMessages(prev => [
      ...prev,
      {
        role: 'user',
        content: userMessage,
        timestamp: new Date(),
        scenarioSnapshot: messageSnapshot,
        attachments: filesToSend.map(f => ({ name: f.name, size: f.size, type: f.type })),
      },
    ]);

    setLoading(true);
    setLoadingStartTime(Date.now());

    // Generate requestId BEFORE sending request so we can track progress
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    console.log('[ScenarioChatbot] Generated requestId for progress tracking:', requestId);
    setCurrentRequestId(requestId);

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    // The answer belongs to the chat it was asked in. If the user has since
    // opened another scenario or version, it must not land there (its intent
    // would be applicable to a different graph), nor reset that chat's state.
    const inView = currentView();
    const requestNo = ++requestSeqRef.current;
    const isCurrent = () => inView() && requestSeqRef.current === requestNo;

    // Define the request function to send after SSE connects
    const sendRequest = async () => {
      console.log('[ScenarioChatbot] Executing API request');
      try {
        // Send message to backend with current scenario context and error context if present
        // Control history inclusion based on user preference
        // Use different API call based on whether files are attached
        let response;
        if (filesToSend.length > 0) {
          response = await ApiScenarioChatbot.sendMessageWithAttachments(
          sessionId,
          messageWithContext, // Send message with error context
          userId,
          messageSnapshot,
          filesToSend,
          {
            maxRecentMessages: includeHistory ? 10 : 0,
            requestId, // Pass requestId to backend
            signal: abortControllerRef.current.signal,
          }
        );
      } else {
        response = await ApiScenarioChatbot.sendMessage(
          sessionId,
          messageWithContext, // Send message with error context
          userId,
          messageSnapshot,
          {
            maxRecentMessages: includeHistory ? 10 : 0, // Include history based on toggle (0 = no history, 10 = last 10 messages)
            requestId, // Pass requestId to backend
            signal: abortControllerRef.current.signal,
          }
        );
      }

      if (!isCurrent()) return;

      if (response.data.success) {
        const assistantMessage = response.data.data.assistantMessage;
        const assistantResponse = assistantMessage?.content || response.data.data.response;

        console.log('[ScenarioChatbot] Raw LLM response:', assistantResponse);

        // Get pre-parsed intent from backend (backend already parsed it)
        // Try assistantMessage.parsed first (API response structure)
        // Then try assistantMessage.metadata.parsed (database structure for loaded messages)
        const parsedData = assistantMessage?.parsed || assistantMessage?.metadata?.parsed || {};
        const intent = parsedData.scenarioIntent || null;
        const intentError = parsedData.intentError || null;

        // Get explanation with safe fallback
        // Backend should always provide explanation, but fallback just in case
        let explanation = parsedData.explanation;
        if (!explanation && intent) {
          // Generate fallback explanation from intent if backend didn't provide one
          const opCount = intent.operations?.length || 0;
          explanation = opCount > 0
            ? `Applying ${opCount} operation${opCount !== 1 ? 's' : ''}`
            : 'Processing changes';
        } else if (!explanation) {
          // Last resort: try parsing the raw response
          const intentParsed = parseIntentFromLLMResponse(assistantResponse);
          explanation = intentParsed.explanation || 'Processing request';
        }

        console.log('[ScenarioChatbot] Backend-parsed intent:', intent);
        console.log('[ScenarioChatbot] Intent parse error:', intentError);
        console.log('[ScenarioChatbot] Explanation:', explanation);

        // Use explanation for display (backend already removed markers)
        const displayContent = explanation;

        console.log('[ScenarioChatbot] Display content:', displayContent);

        // Update the user message with processed attachments (now includes base64Data for images)
        if (filesToSend.length > 0 && response.data.data.attachments) {
          setMessages(prev => {
            const updated = [...prev];
            // Find the user message we just added (should be second to last)
            const userMessageIndex = updated.length - 1;
            if (updated[userMessageIndex] && updated[userMessageIndex].role === 'user') {
              updated[userMessageIndex] = {
                ...updated[userMessageIndex],
                attachments: response.data.data.attachments // Replace with processed attachments
              };
            }
            return updated;
          });
        }

        // Store intent (don't convert to patches yet - only when user clicks Apply)
        let messageData;
        if (intent) {
          // Intent found - store for later processing
          messageData = {
            role: 'assistant',
            content: displayContent,
            timestamp: new Date(),
            processedAttachments: response.data.data.attachments,
            intent: intent, // Store backend-parsed intent, NOT patches
            intentError: intentError,
          };
          console.log('[ScenarioChatbot] Created message with intent:', messageData);
        } else {
          // No intent found (informational response)
          messageData = {
            role: 'assistant',
            content: displayContent,
            timestamp: new Date(),
            processedAttachments: response.data.data.attachments,
            intent: null,
            intentError: intentError,
          };
          console.log('[ScenarioChatbot] Created message without intent (informational):', messageData);
        }

        // Filter out "missing markers" warnings - these are normal for informational responses
        if (messageData.intentError && (messageData.intentError.includes('missing required') || messageData.intentError.includes('INTENT'))) {
          messageData.intentError = null;
        }

        // Add assistant message to UI with intent for preview (don't auto-apply)
        setMessages(prev => {
          const newMessages = [
            ...prev,
            messageData,
          ];

          // Store pending intent if present
          if (intent) {
            const messageIndex = newMessages.length - 1;
            setPendingIntents(prevPending => ({
              ...prevPending,
              [messageIndex]: { intent: intent, parsed: { intent, explanation, error: intentError } }
            }));
          }

          return newMessages;
        });

        // Show error if intent failed to parse (but only real errors, not "no markers" warnings)
        if (messageData.intentError && !intent) {
          setError(messageData.intentError);
        }
        // Note: Warnings about missing markers are filtered out above because
        // informational responses (like answering questions) legitimately won't have intent
      }
    } catch (err) {
      if (!isCurrent()) return;
      // Check if request was cancelled by user (comprehensive check for different axios versions)
      const isCancelled = err.name === 'CanceledError' ||
                          err.code === 'ERR_CANCELED' ||
                          err.message?.includes('canceled') ||
                          err.__CANCEL__ === true ||
                          !abortControllerRef.current; // If controller is null, it was cancelled by user

      if (isCancelled) {
        // Message already added by handleCancelRequest, no need to add error message
      } else {
        console.error('Failed to send message:', err);
        setError(err.response?.data?.error || 'Failed to send message');
        setMessages(prev => [
          ...prev,
          {
            role: 'system',
            content: 'Sorry, there was an error processing your request. Please try again.',
            timestamp: new Date(),
            isError: true,
          },
        ]);
      }
      } finally {
        if (isCurrent()) {
          setLoading(false);
          setLoadingStartTime(null);
          // Don't clear requestId immediately - let SSE connection receive all messages first
          // Clear after a delay to allow SSE to complete (fallback cleanup)
          // Only while it is still this request's id: a newer request started
          // in the meantime keeps its own progress stream.
          setTimeout(() => {
            setCurrentRequestId((id) => (id === requestId ? null : id));
          }, 5000); // 5 seconds should be enough for SSE to receive all messages
          abortControllerRef.current = null;
        }
      }
    };

    // Store the request to be sent after SSE connects
    console.log('[ScenarioChatbot] Waiting for SSE connection before sending request...');
    pendingRequestRef.current = sendRequest;

    // If SSE is already connected, send immediately
    if (isConnected) {
      console.log('[ScenarioChatbot] SSE already connected, sending request immediately');
      pendingRequestRef.current = null;
      sendRequest();
    }
  };

  const handleCancelRequest = () => {
    // Abort the current HTTP request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setLoading(false);
    setLoadingStartTime(null);
    // Clear requestId after delay to allow SSE to close gracefully — the
    // cancelled one only, not a request sent within that second.
    const cancelledId = currentRequestId;
    setTimeout(() => {
      setCurrentRequestId((id) => (id === cancelledId ? null : id));
    }, 1000);
    setMessages(prev => [
      ...prev,
      {
        role: 'system',
        content: 'Request cancelled by user.',
        timestamp: new Date(),
        isError: true,
      },
    ]);
  };

  const handleApplyPatches = (messageIndex) => {
    // Check if this is a re-apply (changes already applied)
    const message = messages[messageIndex];
    const hasAppliedIntent = message && message.intent && !pendingIntents[messageIndex];

    if (hasAppliedIntent) {
      // RE-APPLY: First revert to previous user message state, then re-apply changes
      console.log('[ScenarioChatbot] Re-apply clicked - reverting to previous state then re-applying');

      // Find the previous user message
      const previousUserMessage = messages[messageIndex - 1];
      if (previousUserMessage && previousUserMessage.role === 'user' && previousUserMessage.scenarioSnapshot) {
        // First revert to the previous state
        try {
          // Extract the actual scenario if it's wrapped in an object
          let actualScenario = previousUserMessage.scenarioSnapshot;
          if (previousUserMessage.scenarioSnapshot.scenario && typeof previousUserMessage.scenarioSnapshot.scenario === 'object') {
            actualScenario = previousUserMessage.scenarioSnapshot.scenario;
          }

          // Validate and fix the snapshot structure
          const processedSnapshot = validateAndFixSnapshot(actualScenario);

          // Validate the scenario structure
          const validation = validateScenarioStructure(processedSnapshot);

          if (!validation.isValid && validation.errors.length > 0) {
            console.warn('Snapshot has validation errors:', validation.errors);
          }

          // Update the scenario in parent component
          if (onScenarioUpdate) {
            onScenarioUpdate(processedSnapshot, validation);
          }

          // Update the ref to the reverted scenario
          currentScenarioRef.current = processedSnapshot;

          console.log('[ScenarioChatbot] Reverted to previous state, now re-applying changes');

          // Add a brief system message about revert (without using handleRevertToSnapshot to avoid extra state updates)
          setMessages(prev => [
            ...prev,
            {
              role: 'system',
              content: '🔄 Reverted to previous state, re-applying changes...',
              timestamp: new Date(),
              isSuccess: true,
            },
          ]);
        } catch (err) {
          console.error('[ScenarioChatbot] Error reverting for re-apply:', err);
          setError(`Failed to revert for re-apply: ${err.message}`);
          return;
        }
      } else {
        console.error('[ScenarioChatbot] No previous user message with snapshot found for revert');
        setError('Cannot re-apply: no previous user message with scenario snapshot found');
        return;
      }

      // Fall through to re-apply logic below
    }

    // Check if we have pending intent (new system)
    const pendingIntent = pendingIntents[messageIndex];

    if (pendingIntent) {
      // NEW SYSTEM: Process intent to patches, then apply
      console.log('[ScenarioChatbot] Processing intent to patches:', pendingIntent.intent);

      // Convert intent to patches using intent processor
      const intentResult = processScenarioIntent(
        pendingIntent.intent,
        currentScenarioRef.current,
        nodesDefinitions,
        tasks
      );

      if (intentResult.errors.length > 0) {
        console.error('[ScenarioChatbot] Intent processing errors:', intentResult.errors);
        const errorMsg = `Intent processing errors: ${intentResult.errors.join(', ')}`;
        setError(errorMsg);
        setErrorMessageIndex(messageIndex); // Store which message failed for retry
        setLastPatchErrors(intentResult.errors.join('\n'));

        // Add error message to conversation
        setMessages(prev => [
          ...prev,
          {
            role: 'system',
            content: `❌ ${errorMsg}`,
            timestamp: new Date(),
            isError: true,
            relatedMessageIndex: messageIndex, // Track which message failed for retry button
          },
        ]);

        return;
      }

      // Create parsed object with patches from intent processor
      const parsed = {
        patches: intentResult.patches,
        explanation: messages[messageIndex].content
      };

      // Apply patches
      applyPatchesAndUpdate(parsed, messageIndex);

      // Remove from pending intents
      setPendingIntents(prev => {
        const updated = { ...prev };
        delete updated[messageIndex];
        return updated;
      });

      // Store patches in message for re-application
      setMessages(prev => {
        const newMessages = [...prev];
        newMessages[messageIndex] = {
          ...newMessages[messageIndex],
          patches: intentResult.patches, // Now store generated patches
          intent: null // Clear intent since it's been processed
        };
        return newMessages;
      });
    } else if (message.intent) {
      // Re-application for intent-based messages - re-process the intent to get fresh patches
      console.log('[ScenarioChatbot] Re-processing intent for re-application');
      const intentResult = processScenarioIntent(
        message.intent,
        currentScenarioRef.current,
        nodesDefinitions,
        tasks
      );

      if (intentResult.errors.length > 0) {
        console.error('[ScenarioChatbot] Intent re-processing errors:', intentResult.errors);
        const errorMsg = `Intent re-processing errors: ${intentResult.errors.join(', ')}`;
        setError(errorMsg);
        setErrorMessageIndex(messageIndex); // Store which message failed for retry
        setLastPatchErrors(intentResult.errors.join('\n'));

        // Add error message to conversation
        setMessages(prev => [
          ...prev,
          {
            role: 'system',
            content: `❌ ${errorMsg}`,
            timestamp: new Date(),
            isError: true,
            relatedMessageIndex: messageIndex, // Track which message failed for retry button
          },
        ]);

        return;
      }

      const parsed = {
        patches: intentResult.patches,
        explanation: message.content
      };

      applyPatchesAndUpdate(parsed, messageIndex);
    } else if (message.patches) {
      // Re-apply stored patches from previously processed intent
      console.log('[ScenarioChatbot] Re-applying stored patches');
      const parsed = {
        patches: message.patches,
        explanation: message.content
      };

      applyPatchesAndUpdate(parsed, messageIndex);
    } else {
      console.error('No patches or intent found for message', messageIndex);
    }
  };

  const applyPatchesAndUpdate = (parsed, messageIndex = null) => {
    try {
      // Use ref to get the latest scenario from parent (avoids stale closure)
      const scenarioToModify = currentScenarioRef.current;

      // Validate currentScenario exists
      if (!scenarioToModify || typeof scenarioToModify !== 'object') {
        const errorMsg = 'Cannot apply patches: current scenario is not available';
        console.error(errorMsg, { scenarioToModify });
        setError(errorMsg);
        setErrorMessageIndex(messageIndex); // Store which message failed for retry
        setLastPatchErrors(errorMsg);

        // Add error message to conversation
        if (messageIndex !== null) {
          setMessages(prev => [
            ...prev,
            {
              role: 'system',
              content: `❌ ${errorMsg}`,
              timestamp: new Date(),
              isError: true,
              relatedMessageIndex: messageIndex, // Track which message failed for retry button
            },
          ]);
        }

        return;
      }

      // Step 1: Apply patches
      // Note: The intent processor already handles sequential processing of operations,
      // so we receive a single combined patches object with all changes
      console.log('[ScenarioChatbot] Applying patches:', {
        add: Object.keys(parsed.patches.add || {}).length,
        update: Object.keys(parsed.patches.update || {}).length,
        remove: (parsed.patches.remove || []).length
      });

      const applyResult = applyPatchesToScenario(scenarioToModify, parsed.patches);

      // Collect all errors to provide feedback to LLM
      const allErrors = [];

      if (!applyResult.success) {
        setError(`Patch application had ${applyResult.changes.errors.length} errors`);
        setErrorMessageIndex(messageIndex); // Store which message failed for retry
        console.error('Patch errors:', applyResult.changes.errors);
        allErrors.push(...applyResult.changes.errors);
      }

      let finalScenario = applyResult.scenario;

      // Step 2: Validate
      const validation = validateScenarioStructure(finalScenario);

      // Step 3: Auto-repair if needed
      let repairResult = null;
      if (!validation.isValid) {
        console.warn('Scenario has validation errors, auto-repairing...');
        repairResult = repairScenarioBidirectionalLinks(finalScenario);
        finalScenario = repairResult.scenario;
      }

      // Step 4: Final validation
      const finalValidation = validateScenarioStructure(finalScenario);

      // Collect validation errors
      if (!finalValidation.isValid && finalValidation.errors.length > 0) {
        allErrors.push(...finalValidation.errors);
      }

      // Store errors for next message context if there were any
      if (allErrors.length > 0) {
        const errorMessage = allErrors.map((err, idx) => `${idx + 1}. ${err}`).join('\n');
        setLastPatchErrors(errorMessage);
      } else {
        // Clear errors if everything succeeded
        setLastPatchErrors(null);
        setError(null);
        setErrorMessageIndex(null);
      }

      // Update the scenario in parent component
      // The parent will re-render and pass back the corrected scenario
      console.log('[ScenarioChatbot] Updating parent with final scenario, node count:', Object.keys(finalScenario).length);
      console.log('[ScenarioChatbot] Changes summary:', {
        added: applyResult.changes.added.length,
        updated: applyResult.changes.updated.length,
        removed: applyResult.changes.removed.length,
        removedIds: applyResult.changes.removed
      });

      if (onScenarioUpdate) {
        onScenarioUpdate(finalScenario, finalValidation);
      } else {
        console.error('[ScenarioChatbot] onScenarioUpdate callback is not defined!');
      }

      // CRITICAL: Update the ref to match the new scenario
      currentScenarioRef.current = finalScenario;

      // Show success message if changes were applied
      if (applyResult.changes.added.length > 0 ||
          applyResult.changes.updated.length > 0 ||
          applyResult.changes.removed.length > 0) {
        setMessages(prev => [
          ...prev,
          {
            role: 'system',
            content: `✅ Changes applied successfully!`,
            timestamp: new Date(),
            isSuccess: true,
          },
        ]);
      }

    } catch (err) {
      console.error('Error applying patches:', err);
      const errorMsg = `Failed to apply patches: ${err.message}`;
      setError(errorMsg);
      setErrorMessageIndex(messageIndex); // Store which message failed for retry
      setLastPatchErrors(errorMsg); // Store exception error for next request
      setMessages(prev => [
        ...prev,
        {
          role: 'system',
          content: `❌ Failed to apply changes: ${err.message}`,
          timestamp: new Date(),
          isError: true,
          relatedMessageIndex: messageIndex, // Track which message failed for retry button
        },
      ]);
    }
  };

  const handleNewSession = async () => {
    try {
      setLoading(true);
      setError(null);
      setErrorMessageIndex(null);
      setLastPatchErrors(null); // Clear error context when starting new session
      await createNewSession();
    } catch (err) {
      console.error('Failed to start new session:', err);
      setError('Failed to start a new session');
    } finally {
      setLoading(false);
    }
  };

  const validateAndFixSnapshot = (snapshot) => {
    // Ensure the snapshot is properly structured
    const processedSnapshot = {};
    const nodeIds = Object.keys(snapshot);

    // First pass: Add deserialized flag and ensure basic structure
    nodeIds.forEach(nodeId => {
      const node = snapshot[nodeId];
      if (!node || typeof node !== 'object') {
        console.warn(`Skipping invalid node: ${nodeId}`);
        return;
      }

      processedSnapshot[nodeId] = {
        ...node,
        data: {
          ...node.data,
          deserialized: true
        },
        nextNodes: Array.isArray(node.nextNodes) ? node.nextNodes : [],
        prevNodes: Array.isArray(node.prevNodes) ? node.prevNodes : []
      };
    });

    // Second pass: Validate node references
    Object.keys(processedSnapshot).forEach(nodeId => {
      const node = processedSnapshot[nodeId];

      // Filter out invalid nextNodes references
      node.nextNodes = node.nextNodes.filter(nextId => {
        const exists = processedSnapshot[nextId];
        if (!exists) {
          console.warn(`Removing invalid nextNode reference: ${nextId} from node ${nodeId}`);
        }
        return exists;
      });

      // Filter out invalid prevNodes references
      node.prevNodes = node.prevNodes.filter(prevId => {
        const exists = processedSnapshot[prevId];
        if (!exists) {
          console.warn(`Removing invalid prevNode reference: ${prevId} from node ${nodeId}`);
        }
        return exists;
      });
    });

    return processedSnapshot;
  };

  const handleRetryApply = async () => {
    if (errorMessageIndex === null) {
      console.error('[ScenarioChatbot] No error message index to retry');
      return;
    }

    console.log('[ScenarioChatbot] Reverting and sending retry request to LLM with error context');

    // Get the original user message that led to the error
    let originalUserMessage = null;
    for (let i = errorMessageIndex - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        originalUserMessage = messages[i];
        break;
      }
    }

    if (!originalUserMessage) {
      console.error('[ScenarioChatbot] No original user message found');
      setError('Cannot retry: no original message found');
      return;
    }

    // CRITICAL: First revert to previous stable state before retrying
    // This ensures the new patches will be applied to a clean state
    if (originalUserMessage.scenarioSnapshot) {
      console.log('[ScenarioChatbot] Reverting to previous stable state before retry');
      handleRevertToSnapshot(originalUserMessage.scenarioSnapshot);
    } else {
      console.warn('[ScenarioChatbot] No snapshot available in original user message');
    }

    // Clear error state
    setError(null);
    setErrorMessageIndex(null);

    // Send a new request to LLM with error context
    // The error context is already in lastPatchErrors state, which will be sent automatically
    console.log('[ScenarioChatbot] Sending new request with error context:', lastPatchErrors);

    // Trigger a new message send with the same content
    // This will include the error context from lastPatchErrors
    setInputMessage(`Please fix the previous errors and try again: ${originalUserMessage.content}`);

    // Auto-send after a brief delay to allow state to update
    setTimeout(() => {
      handleSendMessage();
    }, 100);
  };

  const handleRevertToSnapshot = (snapshot) => {
    if (!snapshot) {
      setError('Snapshot not available');
      return;
    }

    try {
      // Extract the actual scenario if it's wrapped in an object
      let actualScenario = snapshot;
      if (snapshot.scenario && typeof snapshot.scenario === 'object') {
        actualScenario = snapshot.scenario;
      }

      // Validate and fix the snapshot structure
      const processedSnapshot = validateAndFixSnapshot(actualScenario);

      // Validate the scenario structure
      const validation = validateScenarioStructure(processedSnapshot);

      if (!validation.isValid && validation.errors.length > 0) {
        console.warn('Snapshot has validation errors:', validation.errors);
        // Continue anyway but warn the user
        setError(`Snapshot restored with warnings: ${validation.errors.slice(0, 2).join(', ')}`);
      }

      // Update the scenario in parent component
      if (onScenarioUpdate) {
        onScenarioUpdate(processedSnapshot, validation);
      }

      // Show success message
      setMessages(prev => [
        ...prev,
        {
          role: 'system',
          content: '🔄 Scenario reverted to selected snapshot successfully!',
          timestamp: new Date(),
          isSuccess: true,
        },
      ]);
    } catch (err) {
      console.error('Error reverting to snapshot:', err);
      setError(`Failed to revert: ${err.message}`);
      setMessages(prev => [
        ...prev,
        {
          role: 'system',
          content: `❌ Failed to revert: ${err.message}`,
          timestamp: new Date(),
          isError: true,
        },
      ]);
    }
  };

  // Calculate inline style for custom positioning and size
  const positionStyle = {
    ...(position ? {
      left: `${position.x}px`,
      top: `${position.y}px`,
      right: 'auto',
      bottom: 'auto',
    } : {}),
    width: `${size.width}px`,
    ...(isCollapsed ? {} : { height: `${size.height}px` }),
    zIndex: 2001, // Force z-index to ensure chatbot appears above everything
  };

  // Intent Preview Component (NEW SYSTEM)
  const IntentPreview = ({ intent, messageIndex, applied }) => {
    const [expandedSections, setExpandedSections] = useState({});

    // Validate intent object
    if (!intent || typeof intent !== 'object' || !Array.isArray(intent.operations)) {
      return null;
    }

    if (intent.operations.length === 0) {
      return null;
    }

    // Group operations by type
    const grouped = {
      ADD_NODE: intent.operations.filter(op => op.type === 'ADD_NODE'),
      UPDATE_NODE: intent.operations.filter(op => op.type === 'UPDATE_NODE'),
      REMOVE_NODE: intent.operations.filter(op => op.type === 'REMOVE_NODE'),
      ADD_BRANCH: intent.operations.filter(op => op.type === 'ADD_BRANCH'),
      REMOVE_BRANCH: intent.operations.filter(op => op.type === 'REMOVE_BRANCH'),
    };

    const toggleSection = (section) => {
      setExpandedSections(prev => ({
        ...prev,
        [section]: !prev[section]
      }));
    };

    const renderAddNodeOp = (op, index) => {
      return (
        <div key={index} className="patch-detail">
          <div className="patch-path">
            <code>{op.nodeType}</code>
            <span className="node-type-badge">Add Node</span>
          </div>
          <div className="patch-value">
            <div className="patch-new-value">
              <span className="patch-label">Position:</span>
              <pre>{JSON.stringify(op.position, null, 2)}</pre>
            </div>
            <div className="patch-new-value">
              <span className="patch-label">Data:</span>
              <pre>{JSON.stringify(op.data, null, 2)}</pre>
            </div>
          </div>
        </div>
      );
    };

    const renderUpdateNodeOp = (op, index) => {
      return (
        <div key={index} className="patch-detail">
          <div className="patch-path">
            <code>{op.nodeId}</code>
            <span className="node-type-badge">Update Node</span>
          </div>
          <div className="patch-value">
            <div className="patch-new-value">
              <span className="patch-label">Updates:</span>
              <pre>{JSON.stringify(op.updates, null, 2)}</pre>
            </div>
          </div>
        </div>
      );
    };

    const renderRemoveNodeOp = (op, index) => {
      return (
        <div key={index} className="patch-detail">
          <div className="patch-path">
            <code>{op.nodeId}</code>
            <span className="node-type-badge">Remove Node</span>
          </div>
          <div className="patch-value">
            <div className="patch-removed">
              <span className="patch-label">Node will be removed</span>
            </div>
          </div>
        </div>
      );
    };

    const renderAddBranchOp = (op, index) => {
      return (
        <div key={index} className="patch-detail">
          <div className="patch-path">
            <code>Branch Logic</code>
            <span className="node-type-badge">Add Branch</span>
          </div>
          <div className="patch-value">
            <div className="patch-new-value">
              <span className="patch-label">Condition Source:</span>
              <pre>{op.conditionSource}</pre>
            </div>
            <div className="patch-new-value">
              <span className="patch-label">Branches:</span>
              <pre>{JSON.stringify(op.branches.map(b => ({ condition: b.condition, nodeCount: b.nodes?.length || 0 })), null, 2)}</pre>
            </div>
          </div>
        </div>
      );
    };

    const renderRemoveBranchOp = (op, index) => {
      return (
        <div key={index} className="patch-detail">
          <div className="patch-path">
            <code>{op.nodeId}</code>
            <span className="node-type-badge">Remove Branch</span>
          </div>
          <div className="patch-value">
            <div className="patch-removed">
              <span className="patch-label">Entire if statement will be removed (condition + all branches + close)</span>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="patch-preview-container">
        <div className="patch-preview-header">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="patch-icon">
            <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z"/>
          </svg>
          <span className="patch-preview-title">
            {applied ? 'Applied Changes' : 'Proposed Changes'}
          </span>
          <span className="patch-count-badge">
            {grouped.ADD_NODE.length > 0 && <span className="add-count">+{grouped.ADD_NODE.length}</span>}
            {grouped.UPDATE_NODE.length > 0 && <span className="replace-count">~{grouped.UPDATE_NODE.length}</span>}
            {grouped.REMOVE_NODE.length > 0 && <span className="remove-count">-{grouped.REMOVE_NODE.length}</span>}
            {grouped.ADD_BRANCH.length > 0 && <span className="add-count">🔀{grouped.ADD_BRANCH.length}</span>}
            {grouped.REMOVE_BRANCH.length > 0 && <span className="remove-count">🔀-{grouped.REMOVE_BRANCH.length}</span>}
          </span>
        </div>

        {grouped.ADD_NODE.length > 0 && (
          <div className="patch-section">
            <div
              className="patch-section-header add"
              onClick={() => toggleSection('add')}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
                className={`chevron ${expandedSections.add ? 'expanded' : ''}`}
              >
                <path d="M7,10l5,5l5-5H7z"/>
              </svg>
              <span className="patch-section-title">
                Add Nodes ({grouped.ADD_NODE.length})
              </span>
            </div>
            {expandedSections.add && (
              <div className="patch-section-content">
                {grouped.ADD_NODE.map((op, idx) => renderAddNodeOp(op, idx))}
              </div>
            )}
          </div>
        )}

        {grouped.UPDATE_NODE.length > 0 && (
          <div className="patch-section">
            <div
              className="patch-section-header replace"
              onClick={() => toggleSection('update')}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
                className={`chevron ${expandedSections.update ? 'expanded' : ''}`}
              >
                <path d="M7,10l5,5l5-5H7z"/>
              </svg>
              <span className="patch-section-title">
                Update Nodes ({grouped.UPDATE_NODE.length})
              </span>
            </div>
            {expandedSections.update && (
              <div className="patch-section-content">
                {grouped.UPDATE_NODE.map((op, idx) => renderUpdateNodeOp(op, idx))}
              </div>
            )}
          </div>
        )}

        {grouped.REMOVE_NODE.length > 0 && (
          <div className="patch-section">
            <div
              className="patch-section-header remove"
              onClick={() => toggleSection('remove')}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
                className={`chevron ${expandedSections.remove ? 'expanded' : ''}`}
              >
                <path d="M7,10l5,5l5-5H7z"/>
              </svg>
              <span className="patch-section-title">
                Remove Nodes ({grouped.REMOVE_NODE.length})
              </span>
            </div>
            {expandedSections.remove && (
              <div className="patch-section-content">
                {grouped.REMOVE_NODE.map((op, idx) => renderRemoveNodeOp(op, idx))}
              </div>
            )}
          </div>
        )}

        {grouped.ADD_BRANCH.length > 0 && (
          <div className="patch-section">
            <div
              className="patch-section-header add"
              onClick={() => toggleSection('branch')}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
                className={`chevron ${expandedSections.branch ? 'expanded' : ''}`}
              >
                <path d="M7,10l5,5l5-5H7z"/>
              </svg>
              <span className="patch-section-title">
                Add Branches ({grouped.ADD_BRANCH.length})
              </span>
            </div>
            {expandedSections.branch && (
              <div className="patch-section-content">
                {grouped.ADD_BRANCH.map((op, idx) => renderAddBranchOp(op, idx))}
              </div>
            )}
          </div>
        )}

        {grouped.REMOVE_BRANCH.length > 0 && (
          <div className="patch-section">
            <div
              className="patch-section-header remove"
              onClick={() => toggleSection('removeBranch')}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
                className={`chevron ${expandedSections.removeBranch ? 'expanded' : ''}`}
              >
                <path d="M7,10l5,5l5-5H7z"/>
              </svg>
              <span className="patch-section-title">
                Remove Branches ({grouped.REMOVE_BRANCH.length})
              </span>
            </div>
            {expandedSections.removeBranch && (
              <div className="patch-section-content">
                {grouped.REMOVE_BRANCH.map((op, idx) => renderRemoveBranchOp(op, idx))}
              </div>
            )}
          </div>
        )}

        <div className="patch-preview-actions">
          <button
            onClick={() => handleApplyPatches(messageIndex)}
            className={`apply-patches-button ${applied ? 'reapply' : ''}`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              {applied ? (
                <path d="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"/>
              ) : (
                <path d="M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z"/>
              )}
            </svg>
            {applied ? 'Re-apply Changes' : 'Apply Changes'}
          </button>
        </div>
      </div>
    );
  };

  // Patch Preview Component (OLD SYSTEM - for backward compatibility)
  const PatchPreview = ({ patches, messageIndex, applied }) => {
    const [expandedSections, setExpandedSections] = useState({});

    // Validate patches object
    if (!patches || typeof patches !== 'object') return null;

    // The patches object has structure: { add: {...}, update: {...}, remove: [...] }
    const hasAdd = patches.add && typeof patches.add === 'object' && Object.keys(patches.add).length > 0;
    const hasUpdate = patches.update && typeof patches.update === 'object' && Object.keys(patches.update).length > 0;
    const hasRemove = patches.remove && Array.isArray(patches.remove) && patches.remove.length > 0;

    if (!hasAdd && !hasUpdate && !hasRemove) return null;

    // Convert to array format for display
    const addedNodes = hasAdd ? Object.entries(patches.add) : [];
    const updatedNodes = hasUpdate ? Object.entries(patches.update) : [];
    const removedNodes = hasRemove ? patches.remove : [];

    const grouped = {
      add: addedNodes,
      update: updatedNodes,
      remove: removedNodes,
    };

    const toggleSection = (section) => {
      setExpandedSections(prev => ({
        ...prev,
        [section]: !prev[section]
      }));
    };

    const renderAddedNode = ([nodeId, nodeData], index) => {
      return (
        <div key={index} className="patch-detail">
          <div className="patch-path">
            <code>{nodeId}</code>
            {nodeData.type && <span className="node-type-badge">{nodeData.type}</span>}
          </div>
          <div className="patch-value">
            <div className="patch-new-value">
              <span className="patch-label">Node Data:</span>
              <pre>{JSON.stringify(nodeData, null, 2)}</pre>
            </div>
          </div>
        </div>
      );
    };

    const renderUpdatedNode = ([nodeId, updates], index) => {
      return (
        <div key={index} className="patch-detail">
          <div className="patch-path">
            <code>{nodeId}</code>
          </div>
          <div className="patch-value">
            {updates.set && (
              <div className="patch-new-value">
                <span className="patch-label">Fields Updated:</span>
                <pre>{JSON.stringify(updates.set, null, 2)}</pre>
              </div>
            )}
            {updates.links && (
              <div className="patch-new-value">
                <span className="patch-label">Links Updated:</span>
                <pre>{JSON.stringify(updates.links, null, 2)}</pre>
              </div>
            )}
          </div>
        </div>
      );
    };

    const renderRemovedNode = (nodeId, index) => {
      return (
        <div key={index} className="patch-detail">
          <div className="patch-path">
            <code>{nodeId}</code>
          </div>
          <div className="patch-value">
            <div className="patch-removed">
              <span className="patch-label">Node Removed</span>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="patch-preview-container">
        <div className="patch-preview-header">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="patch-icon">
            <path d="M22.7,19l-9.1-9.1c0.9-2.3,0.4-5-1.5-6.9c-2-2-5-2.4-7.4-1.3L9,6L6,9L1.6,4.7C0.4,7.1,0.9,10.1,2.9,12.1 c1.9,1.9,4.6,2.4,6.9,1.5l9.1,9.1c0.4,0.4,1,0.4,1.4,0l2.3-2.3C22.9,20,22.9,19.3,22.7,19z"/>
          </svg>
          <span className="patch-preview-title">
            {applied ? 'Applied Changes' : 'Proposed Changes'}
          </span>
          <span className="patch-count-badge">
            {grouped.add.length > 0 && <span className="add-count">+{grouped.add.length}</span>}
            {grouped.update.length > 0 && <span className="replace-count">~{grouped.update.length}</span>}
            {grouped.remove.length > 0 && <span className="remove-count">-{grouped.remove.length}</span>}
          </span>
        </div>

        {grouped.add.length > 0 && (
          <div className="patch-section">
            <div
              className="patch-section-header add"
              onClick={() => toggleSection('add')}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
                className={`chevron ${expandedSections.add ? 'expanded' : ''}`}
              >
                <path d="M7,10l5,5l5-5H7z"/>
              </svg>
              <span className="patch-section-title">
                Added Nodes ({grouped.add.length})
              </span>
            </div>
            {expandedSections.add && (
              <div className="patch-section-content">
                {grouped.add.map((node, idx) => renderAddedNode(node, idx))}
              </div>
            )}
          </div>
        )}

        {grouped.update.length > 0 && (
          <div className="patch-section">
            <div
              className="patch-section-header replace"
              onClick={() => toggleSection('update')}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
                className={`chevron ${expandedSections.update ? 'expanded' : ''}`}
              >
                <path d="M7,10l5,5l5-5H7z"/>
              </svg>
              <span className="patch-section-title">
                Updated Nodes ({grouped.update.length})
              </span>
            </div>
            {expandedSections.update && (
              <div className="patch-section-content">
                {grouped.update.map((node, idx) => renderUpdatedNode(node, idx))}
              </div>
            )}
          </div>
        )}

        {grouped.remove.length > 0 && (
          <div className="patch-section">
            <div
              className="patch-section-header remove"
              onClick={() => toggleSection('remove')}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
                className={`chevron ${expandedSections.remove ? 'expanded' : ''}`}
              >
                <path d="M7,10l5,5l5-5H7z"/>
              </svg>
              <span className="patch-section-title">
                Removed Nodes ({grouped.remove.length})
              </span>
            </div>
            {expandedSections.remove && (
              <div className="patch-section-content">
                {grouped.remove.map((nodeId, idx) => renderRemovedNode(nodeId, idx))}
              </div>
            )}
          </div>
        )}

        <div className="patch-preview-actions">
          <button
            onClick={() => handleApplyPatches(messageIndex)}
            className={`apply-patches-button ${applied ? 'reapply' : ''}`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              {applied ? (
                <path d="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"/>
              ) : (
                <path d="M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z"/>
              )}
            </svg>
            {applied ? 'Re-apply Changes' : 'Apply Changes'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div
      ref={chatbotRef}
      className={`scenario-chatbot-container ${isCollapsed ? 'collapsed' : ''} ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
      style={positionStyle}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragOver && (
        <div className="drag-overlay">
          <svg viewBox="0 0 24 24" fill="currentColor" className="drag-overlay-icon">
            <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"/>
          </svg>
          <span className="drag-overlay-text">Drop files to attach</span>
        </div>
      )}
      <div className="scenario-chatbot-inner">
        {/* Header */}
        <div
          className="reports-chatbot-header"
          onMouseDown={handleMouseDown}
        >
          <div className="reports-chatbot-header-title">
            Scenario Assistant
            <button
              className="reports-chatbot-header-title-dropdown"
              onClick={(e) => {
                e.stopPropagation();
                setIsCollapsed(!isCollapsed);
              }}
              title={isCollapsed ? 'Expand' : 'Collapse'}
              aria-label={isCollapsed ? 'Expand' : 'Collapse'}
            >
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 16 16" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
                style={{ 
                  transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}
              >
                <path d="M2.21973 7.03027C1.92683 6.73738 1.92683 6.26262 2.21973 5.96973C2.51262 5.67683 2.98738 5.67683 3.28027 5.96973L7.75 10.4395L12.2197 5.96973C12.5126 5.67683 12.9874 5.67683 13.2803 5.96973C13.5732 6.26262 13.5732 6.73738 13.2803 7.03027L8.28027 12.0303C7.98738 12.3232 7.51262 12.3232 7.21973 12.0303L2.21973 7.03027Z" fill="#B2B2C7"/>
              </svg>
            </button>
          </div>
          <div className="reports-chatbot-header-actions">
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'new-session',
                    label: 'New session',
                    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"/>
                    </svg>,
                    onClick: ({ domEvent }) => {
                      domEvent?.stopPropagation();
                      handleNewSession();
                    }
                  },
                  {
                    key: 'history',
                    label: includeHistory ? 'Exclude chat history' : 'Include chat history',
                    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      {includeHistory ? (
                        <path d="M13,3A9,9 0 0,0 4,12H1L4.89,15.89L4.96,16.03L9,12H6A7,7 0 0,1 13,5A7,7 0 0,1 20,12A7,7 0 0,1 13,19C11.07,19 9.32,18.21 8.06,16.94L6.64,18.36C8.27,20 10.5,21 13,21A9,9 0 0,0 22,12A9,9 0 0,0 13,3Z"/>
                      ) : (
                        <path d="M13,3A9,9 0 0,0 4,12H1L4.89,15.89L4.96,16.03L9,12H6A7,7 0 0,1 13,5A7,7 0 0,1 20,12A7,7 0 0,1 13,19C11.07,19 9.32,18.21 8.06,16.94L6.64,18.36C8.27,20 10.5,21 13,21A9,9 0 0,0 22,12A9,9 0 0,0 13,3M15.89,10.59L14.5,9.17L12,11.67V16H13.5V12.34L15.89,10.59Z"/>
                      )}
                    </svg>,
                    onClick: ({ domEvent }) => {
                      domEvent?.stopPropagation();
                      setIncludeHistory(!includeHistory);
                    }
                  }
                ]
              }}
              trigger={['click']}
              placement="bottomRight"
            >
              <button
                className="reports-chatbot-icon-button"
                title="More options"
                aria-label="More options"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,16A2,2 0 0,1 14,18A2,2 0 0,1 12,20A2,2 0 0,1 10,18A2,2 0 0,1 12,16M12,10A2,2 0 0,1 14,12A2,2 0 0,1 12,14A2,2 0 0,1 10,12A2,2 0 0,1 12,10M12,4A2,2 0 0,1 14,6A2,2 0 0,1 12,8A2,2 0 0,1 10,6A2,2 0 0,1 12,4Z"/>
                </svg>
              </button>
            </Dropdown>
            <button
              className="reports-chatbot-icon-button"
              onClick={(e) => {
                e.stopPropagation();
                setIsCollapsed(!isCollapsed);
              }}
              title={isCollapsed ? 'Expand' : 'Minimize'}
              aria-label={isCollapsed ? 'Expand' : 'Minimize'}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19,13H5V11H19V13Z"/>
              </svg>
            </button>
            <button
              className="reports-chatbot-icon-button"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              title="Close"
              aria-label="Close"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41z"/>
              </svg>
            </button>
          </div>
        </div>

      {/* Error Alert */}
      {!isCollapsed && error && (
        <div className="chatbot-alert error">
          <span>{error}</span>
          <div className="chatbot-alert-actions">
            {errorMessageIndex !== null && (() => {
              // Find the previous user message with snapshot for revert
              let previousUserMessage = null;
              for (let i = errorMessageIndex - 1; i >= 0; i--) {
                if (messages[i]?.role === 'user' && messages[i]?.scenarioSnapshot) {
                  previousUserMessage = messages[i];
                  break;
                }
              }

              return (
                <>
                  {previousUserMessage && (
                    <button
                      onClick={() => {
                        setError(null);
                        setErrorMessageIndex(null);
                        handleRevertToSnapshot(previousUserMessage.scenarioSnapshot);
                      }}
                      aria-label="Revert to previous state"
                      className="chatbot-revert-button"
                      title="Revert to previous scenario state"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12.5,8c-2.65,0-5.05,0.99-6.9,2.6L2,7v9h9l-3.62-3.62c1.39-1.16,3.16-1.88,5.12-1.88c3.54,0,6.55,2.31,7.6,5.5l2.37-0.78C21.08,11.03,17.15,8,12.5,8z"/>
                      </svg>
                      Revert
                    </button>
                  )}
                  <button
                    onClick={handleRetryApply}
                    aria-label="Try again"
                    className="chatbot-retry-button"
                    title="Retry applying changes"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"/>
                    </svg>
                    Try again
                  </button>
                </>
              );
            })()}
            <button
              onClick={() => {
                setError(null);
                setErrorMessageIndex(null);
              }}
              aria-label="Close alert"
              className="chatbot-close-button"
            >×</button>
          </div>
        </div>
      )}


      {/* Messages */}
      {!isCollapsed && (
        <div className="chatbot-messages-container">
          {messages.map((msg, index) => {
            const isUser = msg.role === 'user';
            const isSystem = msg.role === 'system';
            const isAssistant = msg.role === 'assistant';

            let messageClass = 'chatbot-message';
            if (isUser) messageClass += ' user';
            else if (isAssistant) messageClass += ' assistant';
            else if (isSystem) {
              messageClass += ' system';
              if (msg.isError) messageClass += ' error';
              else if (msg.isSuccess) messageClass += ' success';
              else messageClass += ' info';
            }

            return (
              <div key={index} className="chatbot-message-wrapper">
                <div className={messageClass}>
                  <div className="chatbot-message-header">
                    {isUser ? 'You' : isSystem ? 'System' : 'Assistant'}
                  </div>
                  <div className="chatbot-message-content">
                  <ReactMarkdown>{msg.content}</ReactMarkdown>

                  {/* Display intent preview for assistant messages with intent (NEW SYSTEM) */}
                  {isAssistant && msg.intent && (
                    <IntentPreview
                      intent={msg.intent}
                      messageIndex={index}
                      applied={!pendingIntents[index]}
                    />
                  )}

                  {/* Display patch preview for assistant messages with patches (from processed intents) */}
                  {isAssistant && msg.patches && !msg.intent && (
                    <PatchPreview
                      patches={msg.patches}
                      messageIndex={index}
                      applied={true}
                    />
                  )}

                  {/* Display intent error if present */}
                  {isAssistant && msg.intentError && !msg.intent && (
                    <div className="patch-error">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12,2L1,21H23M12,6L19.53,19H4.47M11,10V14H13V10M11,16V18H13V16"/>
                      </svg>
                      <span>Error parsing intent: {msg.intentError}</span>
                    </div>
                  )}

                  {/* Display patch error if present (backward compatibility) */}
                  {isAssistant && msg.patchError && !msg.patches && !msg.intent && (
                    <div className="patch-error">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12,2L1,21H23M12,6L19.53,19H4.47M11,10V14H13V10M11,16V18H13V16"/>
                      </svg>
                      <span>Error parsing patches: {msg.patchError}</span>
                    </div>
                  )}
                </div>
                
                {!sessionId && isAssistant && index === messages.length - 1 && (
                  <Button
                    onClick={handleNewSession}
                    disabled={loading}
                    type="primary"
                    className="chatbot-new-session-button"
                    icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"/>
                    </svg>}
                  >
                    New session
                  </Button>
                )}
                <div className="chatbot-message-footer">
                  {!isUser && (
                    <div className="chatbot-timestamp">
                      {new Date(msg.timestamp).toLocaleTimeString()}
                    </div>
                  )}
                  {isSystem && msg.isError && msg.relatedMessageIndex !== undefined && (
                    <div className="chatbot-error-actions">
                      {/* Find previous user message for revert */}
                      {(() => {
                        // Find the user message that came before the failed assistant message
                        let previousUserMessage = null;

                        // Search backwards from the failed message index
                        for (let i = msg.relatedMessageIndex - 1; i >= 0; i--) {
                          if (messages[i].role === 'user' && messages[i].scenarioSnapshot) {
                            previousUserMessage = messages[i];
                            break;
                          }
                        }

                        return previousUserMessage && (
                          <button
                            onClick={() => {
                              setError(null);
                              setErrorMessageIndex(null);
                              handleRevertToSnapshot(previousUserMessage.scenarioSnapshot);
                            }}
                            title="Revert to previous scenario state"
                            aria-label="Revert to previous scenario state"
                            className="chatbot-revert-button-inline"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12.5,8c-2.65,0-5.05,0.99-6.9,2.6L2,7v9h9l-3.62-3.62c1.39-1.16,3.16-1.88,5.12-1.88c3.54,0,6.55,2.31,7.6,5.5l2.37-0.78C21.08,11.03,17.15,8,12.5,8z"/>
                            </svg>
                            Revert
                          </button>
                        );
                      })()}
                      <button
                        onClick={async () => {
                          // Get the original user message that led to the error
                          let originalUserMessage = null;
                          for (let i = msg.relatedMessageIndex - 1; i >= 0; i--) {
                            if (messages[i].role === 'user') {
                              originalUserMessage = messages[i];
                              break;
                            }
                          }

                          if (!originalUserMessage) {
                            console.error('[ScenarioChatbot] No original user message found');
                            return;
                          }

                          // CRITICAL: First revert to previous stable state before retrying
                          // This ensures the new patches will be applied to a clean state
                          if (originalUserMessage.scenarioSnapshot) {
                            console.log('[ScenarioChatbot] Reverting to previous stable state before retry (inline)');
                            handleRevertToSnapshot(originalUserMessage.scenarioSnapshot);
                          } else {
                            console.warn('[ScenarioChatbot] No snapshot available in original user message (inline)');
                          }

                          setError(null);
                          setErrorMessageIndex(null);

                          // Send a new request to LLM with error context
                          setInputMessage(`Please fix the previous errors and try again: ${originalUserMessage.content}`);

                          // Auto-send after a brief delay
                          setTimeout(() => {
                            handleSendMessage();
                          }, 100);
                        }}
                        title="Send request to LLM to fix errors"
                        aria-label="Send request to LLM to fix errors"
                        className="chatbot-retry-button-inline"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"/>
                        </svg>
                        Try again
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Display attachments for user messages */}
              {isUser && msg.attachments && msg.attachments.length > 0 && (
                <div className="chatbot-attachments-list" style={{ marginTop: '8px', justifyContent: 'flex-start' }}>
                  {msg.attachments.map((att, attIdx) => (
                    <div key={attIdx} className="chatbot-attachment-item-sent">
                      <div className="attachment-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                        </svg>
                      </div>
                      <div className="attachment-info">
                        <span className="attachment-name">{att.fileName || att.name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Display processed attachments for assistant messages */}
              {isAssistant && msg.processedAttachments && msg.processedAttachments.length > 0 && (
                <div style={{ marginTop: '8px', width: '100%' }}>
                  <div style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '4px', opacity: 0.7, textAlign: 'left' }}>
                    Processed {msg.processedAttachments.length} file(s):
                  </div>
                  <div className="chatbot-attachments-list" style={{ justifyContent: 'flex-start', width: '100%' }}>
                    {msg.processedAttachments.map((att, attIdx) => (
                      <div key={attIdx} className="chatbot-attachment-item-sent">
                        <div className="attachment-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                          </svg>
                        </div>
                        <div className="attachment-info">
                          <span className="attachment-name">{att.fileName}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {isUser && msg.scenarioSnapshot && (
                <button
                  onClick={() => handleRevertToSnapshot(msg.scenarioSnapshot)}
                  title="Revert to this scenario state"
                  aria-label="Revert to this scenario state"
                  className="chatbot-revert-button"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.5,8c-2.65,0-5.05,0.99-6.9,2.6L2,7v9h9l-3.62-3.62c1.39-1.16,3.16-1.88,5.12-1.88c3.54,0,6.55,2.31,7.6,5.5l2.37-0.78C21.08,11.03,17.15,8,12.5,8z"/>
                  </svg>
                  Revert here
                </button>
              )}
            </div>
            );
          })}
          {loading && (
            <div className="chatbot-loading-message">
              <div className="chatbot-loading-content">
                <LoadingOutlined style={{ fontSize: '20px', color: '#667eea' }} />
                <div className="chatbot-loading-text">
                  <span className="chatbot-loading-stage">
                    {stageName || 'Thinking...'}
                  </span>
                  <span className="chatbot-loading-elapsed">
                    {elapsedDisplaySeconds}s
                  </span>
                </div>
              </div>
              {loadingDuration >= 20 && (
                <button
                  onClick={handleCancelRequest}
                  className="chatbot-cancel-button"
                  title="Cancel request"
                >
                  Cancel
                </button>
              )}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Input */}
      {!isCollapsed && (
        <div className="chatbot-input-wrapper">
          {/* Error context indicator */}
          {lastPatchErrors && (
            <div className="chatbot-error-context">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="chatbot-error-context-icon">
                <path d="M12,2L1,21H23M12,6L19.53,19H4.47M11,10V14H13V10M11,16V18H13V16"/>
              </svg>
              <span>Previous errors will be sent with your next message for LLM to fix</span>
            </div>
          )}

          {/* History status indicator */}
          {!includeHistory && (
            <div className="chatbot-history-notice">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="chatbot-history-notice-icon">
                <path d="M13,3A9,9 0 0,0 4,12H1L4.89,15.89L4.96,16.03L9,12H6A7,7 0 0,1 13,5A7,7 0 0,1 20,12A7,7 0 0,1 13,19C11.07,19 9.32,18.21 8.06,16.94L6.64,18.36C8.27,20 10.5,21 13,21A9,9 0 0,0 22,12A9,9 0 0,0 13,3M15.89,10.59L14.5,9.17L12,11.67V16H13.5V12.34L15.89,10.59Z"/>
              </svg>
              <span>Chat history excluded - LLM will only see current message</span>
            </div>
          )}

          <ChatInputAreaWithUpload
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onSend={handleSendMessage}
            disabled={loading || !sessionId}
            placeholder="Describe what you want to change..."
            attachedFiles={attachedFiles}
            onFilesChange={setAttachedFiles}
            blobUrlsRef={blobUrlsRef}
          />
        </div>
      )}
      </div>
    </div>
  );
};

export default ScenarioChatbot;
